import { useState } from 'react';
import { Box, Button, Modal, Text, TextArea } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import {
  useCancelTowProRequestMutation,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import styles from './TowProCancelModal.module.css';

type RotationPlacement = 'top' | 'bottom';

const ROTATION_OPTIONS: { value: RotationPlacement; label: string }[] = [
  { value: 'top', label: 'Top of the list' },
  { value: 'bottom', label: 'Bottom of the list' },
];

const CANCEL_ERROR_MESSAGE =
  'Unable to cancel the tow request. Please try again.';

interface TowProCancelModalProps {
  request: TowProRequestResponse;
  showModal: boolean;
  onClose: () => void;
}

/**
 * Cancels an active tow request. Requires a cancellation reason and where the
 * released vendor re-enters the rotation list, both forwarded to TowPro.
 */
function TowProCancelModal({
  request,
  showModal,
  onClose,
}: TowProCancelModalProps): JSX.Element {
  const [cancelReason, setCancelReason] = useState('');
  const [rotationPlacement, setRotationPlacement] =
    useState<RotationPlacement | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelTowProRequest, { isLoading }] = useCancelTowProRequestMutation();

  const canConfirm =
    cancelReason.trim().length > 0 && rotationPlacement !== null && !isLoading;

  // Reset transient state so a stale error/reason doesn't persist when the
  // modal is reopened.
  const handleClose = (): void => {
    setCancelError(null);
    setCancelReason('');
    setRotationPlacement(null);
    onClose();
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (!canConfirm || rotationPlacement === null) {
      return;
    }

    setCancelError(null);
    try {
      await cancelTowProRequest({
        requestId: request.id,
        body: { reason: cancelReason.trim(), rotationPlacement },
      }).unwrap();
      handleClose();
    } catch {
      // Surface a user-facing error so the modal can render an error box and
      // stay open for the user to retry.
      setCancelError(CANCEL_ERROR_MESSAGE);
    }
  };

  return (
    <Modal
      id="towpro-cancel-request-modal"
      isOpened={showModal}
      onClose={handleClose}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={handleClose}>
        <Text color="primaryText" size="large">
          Cancel Tow Request
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text color="secondaryText" size="normal">
          Reason for cancellation
        </Text>
        <Box mt={2}>
          <TextArea
            id="towpro-cancel-reason"
            name="towpro-cancel-reason"
            rows={3}
            fontSize="14px"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
          />
        </Box>
        <Box mt={3}>
          <Text color="secondaryText" size="normal">
            Return the tow vendor to the rotation:
          </Text>
          <div
            className={styles.rotationOptions}
            role="radiogroup"
            aria-label="Vendor rotation placement"
          >
            {ROTATION_OPTIONS.map(option => (
              <label key={option.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="towpro-cancel-rotation"
                  value={option.value}
                  checked={rotationPlacement === option.value}
                  onChange={() => setRotationPlacement(option.value)}
                  data-name={`towpro-cancel-rotation-${option.value}`}
                />
                {option.label}
              </label>
            ))}
          </div>
        </Box>
      </ModalBody>
      {cancelError && (
        <Box mb={1} p={3} bg="lightDanger" borderRadius="4px" role="alert">
          <Text color="background" size="small">
            {cancelError}
          </Text>
        </Box>
      )}
      <ModalFooter>
        <Box horizontal justify="center" width="100%">
          <Button grow btnType="tertiary" mx={0} onClick={handleClose}>
            Keep request
          </Button>
          <Button
            grow
            btnType="warning_primary"
            ml={2}
            onClick={onConfirmCancel}
            disabled={!canConfirm}
            dataName="towpro-cancel-confirm-button"
          >
            Cancel
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
}

export default TowProCancelModal;
